import { useMemo, useState } from "react";
import { Check, Pencil, X, Copy, Search, Languages, Download, Loader2, Sparkles } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const TONE_ROTATION = ["tone-lavender", "tone-powder", "tone-sage", "tone-apricot", "tone-blush"];

function fmtTs(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// When the transcript has only flat text and no segments, split it into
// sentence-paragraph blocks so the user still gets a row-oriented view.
function synthesizeSegmentsFromText(text) {
  if (!text || !text.trim()) return [];
  // Split into sentences; group ~3 per row so each row is digestible.
  const sentences = text
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const groupSize = 3;
  const out = [];
  for (let i = 0; i < sentences.length; i += groupSize) {
    out.push({
      start: null,
      end: null,
      speaker: "Speaker",
      text: sentences.slice(i, i + groupSize).join(" "),
      synthetic: true,
    });
  }
  return out;
}

function highlight(text, q) {
  if (!q) return text;
  const safe = q.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
  const parts = text.split(new RegExp(`(${safe})`, "ig"));
  return (parts || []).map((p, i) =>
    p.toLowerCase() === q.toLowerCase() ? (
      <mark key={i} className="bg-yellow-200/70 rounded px-0.5">{p}</mark>
    ) : (
      <span key={i}>{p}</span>
    ),
  );
}

export default function TranscriptTimeline({ transcript, meeting, onUpdated }) {
  const [editing, setEditing] = useState(null); // speaker label being edited
  const [editValue, setEditValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [lang, setLang] = useState("original"); // 'original' | 'en'
  const [translating, setTranslating] = useState(false);

  const hasTranslation =
    Boolean((transcript?.transcript_text_en || "").trim()) ||
    Boolean((transcript?.segments_en_lines || []).length);

  const { rows, speakers, tones, isSynthetic } = useMemo(() => {
    const rawSegs = (transcript?.segments || []).filter(
      (s) => (s.text || "").trim(),
    );
    const enLines = transcript?.segments_en_lines || [];
    let segs = rawSegs;
    let synthetic = false;
    if (segs.length === 0 && transcript?.transcript_text) {
      segs = synthesizeSegmentsFromText(transcript.transcript_text);
      synthetic = true;
    }
    // Normalise speaker labels — null/empty becomes "Speaker". Attach en text
    // if present and we're not in the synthesized branch.
    segs = (segs || []).map((s, i) => {
      const enLine = !synthetic && i < enLines.length ? enLines[i] : null;
      return {
        idx: i,
        start: s.start,
        end: s.end,
        text: (s.text || "").trim(),
        textEn: enLine ? String(enLine).trim() : null,
        speaker: (s.speaker || "Speaker").trim() || "Speaker",
        synthetic: !!s.synthetic,
      };
    });
    // Build the ordered, de-duplicated speaker list (preserves first-seen order).
    const seen = new Set();
    const speakerList = [];
    const toneMap = {};
    for (const s of segs) {
      if (!seen.has(s.speaker)) {
        seen.add(s.speaker);
        speakerList.push(s.speaker);
        toneMap[s.speaker] = TONE_ROTATION[(speakerList.length - 1) % TONE_ROTATION.length];
      }
    }
    return { rows: segs, speakers: speakerList, tones: toneMap, isSynthetic: synthetic };
  }, [transcript]);

  // For synthetic transcripts (no real segments), split the EN text into the
  // same pseudo-rows for the toggle to work.
  const syntheticEnRows = (() => {
    if (!isSynthetic || !transcript?.transcript_text_en) return null;
    const synthEn = synthesizeSegmentsFromText(transcript.transcript_text_en);
    return (synthEn || []).map((s) => (s.text || "").trim());
  })();

  // Merge consecutive same-speaker rows into a single "turn", picking text
  // based on the language toggle. The React Compiler memoizes these
  // function-scope computations automatically.
  const turns = (rows || []).reduce((acc, r, i) => {
    let displayText = r.text;
    if (lang === "en") {
      if (isSynthetic && syntheticEnRows && syntheticEnRows[i]) {
        displayText = syntheticEnRows[i];
      } else if (r.textEn) {
        displayText = r.textEn;
      }
    }
    const enriched = { ...r, displayText };
    const last = acc[acc.length - 1];
    if (last && last.speaker === r.speaker) {
      return [
        ...acc.slice(0, -1),
        { ...last, segments: [...last.segments, enriched] },
      ];
    }
    return [...acc, { speaker: r.speaker, segments: [enriched] }];
  }, []);

  // Lookup of speaker analytics for tooltips.
  const speakersAnalytics = (meeting && meeting.analytics && meeting.analytics.speakers) || [];
  const stats = Object.fromEntries(
    speakersAnalytics.filter((sp) => sp && sp.name).map((sp) => [sp.name, sp]),
  );

  async function saveRename(fromLabel) {
    const to = editValue.trim();
    if (!to || to === fromLabel) {
      setEditing(null);
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/transcripts/${meeting.id}/rename-speaker`, {
        from_label: fromLabel,
        to_label: to,
      });
      toast.success(`Renamed “${fromLabel}” → “${to}”`);
      setEditing(null);
      setEditValue("");
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(label) {
    setEditing(label);
    setEditValue(label === "Speaker" ? "" : label);
  }

  async function copyAll() {
    const text = (turns || [])
      .map((t) => `${t.speaker}\n${(t.segments || []).map((s) => s.displayText || s.text).join(" ")}`)
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Transcript copied to clipboard");
    } catch (_e) {
      toast.error("Copy failed");
    }
  }

  async function translateNow() {
    setTranslating(true);
    try {
      await api.post(`/transcripts/${meeting.id}/translate`);
      toast.success("Translation ready");
      setLang("en");
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTranslating(false);
    }
  }

  async function retranslateFromAudio() {
    setTranslating(true);
    try {
      const { data } = await api.post(`/transcripts/${meeting.id}/retranslate-from-audio`);
      toast.success(`High-fidelity translation ready · ${data.segments_en_timed_count || 0} timed segments`);
      setLang("en");
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTranslating(false);
    }
  }

  async function downloadFile(fmt, langChoice) {
    try {
      const res = await api.get(
        `/transcripts/${meeting.id}/download?format=${fmt}&lang=${langChoice}`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(dispo);
      a.download = m ? m[1] : `transcript.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (turns.length === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground bg-card">
        {meeting?.status === "Processing"
          ? "Transcribing your recording — this may take a minute. The transcript will appear here automatically."
          : "No transcript yet. Record the meeting to generate a transcript."}
      </div>
    );
  }

  const totalWords = (rows || []).reduce((a, r) => a + r.text.split(/\s+/).filter(Boolean).length, 0);
  const lastEnd = (rows || []).reduce((a, r) => (Number.isFinite(r.end) ? Math.max(a, r.end) : a), 0);

  return (
    <div className="rounded-3xl bg-card border border-border shadow-card overflow-hidden">
      {/* Header strip — meta + speakers rail */}
      <div className="px-6 pt-5 pb-4 border-b border-border tone-lavender/30 bg-gradient-to-r from-[hsl(var(--tone-lavender)/0.6)] to-[hsl(var(--tone-powder)/0.5)]">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow">Transcript · timeline</div>
            <div className="mt-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-foreground/70 flex items-center gap-3 flex-wrap">
              <span>Language: {transcript?.language_detected || "auto"}</span>
              <span>·</span>
              <span>{speakers.length} {speakers.length === 1 ? "speaker" : "speakers"}</span>
              <span>·</span>
              <span>{totalWords.toLocaleString()} words</span>
              {lastEnd > 0 && (
                <>
                  <span>·</span>
                  <span>{fmtTs(lastEnd)} duration</span>
                </>
              )}
              {isSynthetic && (
                <span className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-900 px-2.5 py-0.5 text-[9px] tracking-[0.18em] border border-amber-200">
                  No diarization · estimated
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Language toggle */}
            <div
              data-testid="transcript-lang-toggle"
              className="inline-flex rounded-full bg-white border border-border p-0.5 text-[10px] uppercase tracking-[0.16em] font-semibold"
            >
              <button
                type="button"
                data-testid="transcript-lang-original"
                onClick={() => setLang("original")}
                className={`px-3 py-1.5 rounded-full transition-colors ${
                  lang === "original"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Original
              </button>
              <button
                type="button"
                data-testid="transcript-lang-english"
                disabled={!hasTranslation}
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 rounded-full transition-colors ${
                  lang === "en"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:hover:text-muted-foreground"
                }`}
                title={hasTranslation ? "English translation" : "Click 'Translate' first"}
              >
                English
              </button>
            </div>

            {!hasTranslation && (
              <Button
                data-testid="transcript-translate-btn"
                type="button"
                variant="outline"
                size="sm"
                onClick={translateNow}
                disabled={translating}
                className="rounded-full h-9 px-3 text-[10px] uppercase tracking-[0.16em] font-semibold"
              >
                {translating ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={2} />
                ) : (
                  <Languages className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                )}
                Translate to English
              </Button>
            )}

            <Button
              data-testid="transcript-retranslate-audio-btn"
              type="button"
              variant="outline"
              size="sm"
              onClick={retranslateFromAudio}
              disabled={translating}
              title="Re-translate by re-running the original audio through the Yanthra AI Agent — best for code-switched languages."
              className="rounded-full h-9 px-3 text-[10px] uppercase tracking-[0.16em] font-semibold"
            >
              {translating ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={2} />
              ) : (
                <Sparkles className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
              )}
              Re-translate from audio
            </Button>

            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={2} />
              <Input
                data-testid="transcript-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search transcript…"
                className="h-9 pl-8 pr-3 rounded-full bg-white border-border text-xs w-56"
              />
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger data-testid="transcript-options-trigger" asChild>
                <Button
                  data-testid="transcript-download-btn"
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full h-9 px-3 text-[10px] uppercase tracking-[0.16em] font-semibold"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                  Download
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-2xl w-56">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                  Original
                </DropdownMenuLabel>
                <DropdownMenuItem
                  data-testid="download-txt-original"
                  onClick={() => downloadFile("txt", "original")}
                  className="cursor-pointer"
                >
                  As .txt
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="download-srt-original"
                  onClick={() => downloadFile("srt", "original")}
                  className="cursor-pointer"
                >
                  As .srt (subtitles)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                  English
                </DropdownMenuLabel>
                <DropdownMenuItem
                  data-testid="download-txt-en"
                  disabled={!hasTranslation}
                  onClick={() => downloadFile("txt", "en")}
                  className="cursor-pointer"
                >
                  As .txt {hasTranslation ? "" : "· translate first"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="download-srt-en"
                  disabled={!hasTranslation}
                  onClick={() => downloadFile("srt", "en")}
                  className="cursor-pointer"
                >
                  As .srt {hasTranslation ? "" : "· translate first"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              data-testid="transcript-copy-btn"
              type="button"
              variant="outline"
              size="sm"
              onClick={copyAll}
              className="rounded-full h-9 px-3 text-[10px] uppercase tracking-[0.16em] font-semibold"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
              Copy
            </Button>
          </div>
        </div>

        {/* Speakers rail — clickable chips that open an inline rename input */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="speakers-rail">
          {(speakers || []).map((sp) => {
            const tone = tones[sp];
            const sStat = stats[sp];
            const isEd = editing === sp;
            return (
              <div key={sp} className="inline-flex">
                {isEd ? (
                  <div className="inline-flex items-stretch rounded-full overflow-hidden border border-primary bg-white shadow-sm">
                    <input
                      autoFocus
                      data-testid={`speaker-edit-input-${sp}`}
                      value={editValue}
                      disabled={busy}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveRename(sp);
                        if (e.key === "Escape") setEditing(null);
                      }}
                      placeholder="Speaker name"
                      className="h-8 px-3 text-xs font-semibold outline-none w-40"
                    />
                    <button
                      type="button"
                      data-testid={`speaker-edit-save-${sp}`}
                      onClick={() => saveRename(sp)}
                      disabled={busy}
                      className="px-2 bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))] disabled:opacity-50"
                      aria-label="Save"
                    >
                      <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </button>
                    <button
                      type="button"
                      data-testid={`speaker-edit-cancel-${sp}`}
                      onClick={() => setEditing(null)}
                      className="px-2 bg-secondary text-foreground/70 hover:bg-secondary/80"
                      aria-label="Cancel"
                    >
                      <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </button>
                  </div>
                ) : (
                  <TooltipProvider delayDuration={120}>
                    <Tooltip>
                      <TooltipTrigger data-testid="transcript-segment-tooltip-trigger" asChild>
                        <button
                          type="button"
                          data-testid={`speaker-chip-${sp}`}
                          onClick={() => startEdit(sp)}
                          className={`inline-flex items-center gap-2 ${tone} rounded-full pl-1 pr-3 py-1 text-xs font-semibold hover:ring-2 hover:ring-primary/30 transition-shadow`}
                        >
                          <span className="w-6 h-6 rounded-full bg-white/70 grid place-items-center text-[10px] font-bold uppercase">
                            {sp.split(/\s+/).map((w) => w[0]).join("").slice(0, 2) || "?"}
                          </span>
                          <span>{sp}</span>
                          {sStat?.percentage != null && (
                            <span className="text-[10px] opacity-70">· {sStat.percentage}%</span>
                          )}
                          <Pencil className="w-3 h-3 opacity-50" strokeWidth={2} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        className="rounded-xl bg-card text-foreground border border-border shadow-lift px-3 py-2 text-xs"
                      >
                        Click to rename
                        {sStat && (
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            {sStat.minutes ?? 0}m spoken · {(sStat.word_count ?? 0).toLocaleString()} words
                          </div>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Timeline body */}
      <div
        className="p-6 max-h-[68vh] overflow-y-auto"
        data-testid="transcript-timeline-body"
      >
        <div className="relative pl-5">
          {/* Vertical rail */}
          <div className="absolute left-[7px] top-1.5 bottom-1.5 w-[2px] bg-border" />
          <ul className="space-y-7">
            {(turns || []).map((turn, ti) => {
              const tone = tones[turn.speaker] || "tone-lavender";
              const initials = turn.speaker.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";
              const sStat = stats[turn.speaker];
              const turnText = (turn.segments || []).map((s) => s.displayText || s.text).join(" ");
              const matchesQuery = !query || turnText.toLowerCase().includes(query.toLowerCase());
              return (
                <li
                  key={ti}
                  data-testid="transcript-turn"
                  className={`relative ${matchesQuery ? "" : "opacity-40"}`}
                >
                  {/* Dot on the rail */}
                  <span
                    className={`absolute -left-5 top-1.5 w-4 h-4 rounded-full ${tone} border-2 border-white shadow-sm`}
                  />
                  {/* Speaker row */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      data-testid="transcript-speaker-chip"
                      onClick={() => startEdit(turn.speaker)}
                      className={`inline-flex items-center gap-2 ${tone} rounded-full pl-1 pr-3 py-1 text-xs font-semibold hover:ring-2 hover:ring-primary/30 transition-shadow`}
                    >
                      <span className="w-6 h-6 rounded-full bg-white/70 grid place-items-center text-[10px] font-bold">
                        {initials}
                      </span>
                      <span>{turn.speaker}</span>
                      <Pencil className="w-3 h-3 opacity-50" strokeWidth={2} />
                    </button>
                    {Number.isFinite(turn.segments[0].start) && (
                      <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground bg-secondary rounded-full px-2 py-0.5">
                        {fmtTs(turn.segments[0].start)}
                      </span>
                    )}
                    {sStat?.percentage != null && (
                      <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">
                        {sStat.percentage}% share
                      </span>
                    )}
                  </div>

                  {/* Segments of this turn — each line with its own tiny timestamp */}
                  <div className="mt-2 space-y-1.5">
                    {(turn.segments || []).map((seg, si) => (
                      <div key={si} className="flex items-start gap-3 group">
                        <span className="shrink-0 mt-[3px] text-[10px] font-mono text-muted-foreground tabular-nums w-12 text-right">
                          {Number.isFinite(seg.start) ? fmtTs(seg.start) : "—"}
                        </span>
                        <p className="text-[14px] leading-relaxed text-foreground/90 min-w-0">
                          {highlight(seg.displayText || seg.text, query)}
                        </p>
                      </div>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
